// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
// Note: Drag and drop functionality will be implemented later
import { 
  Building, 
  Calendar, 
  DollarSign, 
  Mail, 
  Phone, 
  Star, 
  MapPin,
  Users,
  TrendingUp,
  Clock,
  MoreHorizontal
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface WhiteLabelProspect {
  id: string;
  organization_name: string;
  contact_name: string;
  contact_email?: string;
  contact_phone?: string;
  organization_type: string;
  annual_event_volume?: number;
  average_participants_per_event?: number;
  geographic_markets?: string[];
  pipeline_stage: string;
  estimated_value?: number;
  priority_level: string;
  technology_readiness?: number;
  next_action?: string;
  next_action_date?: string;
  tags?: string[];
  created_at: string;
}

interface WhiteLabelPipelineProps {
  searchTerm: string;
}

const pipelineStages = [
  { id: 'prospect', name: 'Prospect', color: 'bg-slate-100' },
  { id: 'demo_requested', name: 'Demo Requested', color: 'bg-blue-100' },
  { id: 'demo_completed', name: 'Demo Completed', color: 'bg-purple-100' },
  { id: 'proposal_sent', name: 'Proposal Sent', color: 'bg-orange-100' },
  { id: 'contract_negotiation', name: 'Negotiation', color: 'bg-yellow-100' },
  { id: 'implementation', name: 'Implementation', color: 'bg-green-100' },
  { id: 'live', name: 'Live', color: 'bg-emerald-100' },
  { id: 'renewal', name: 'Renewal', color: 'bg-indigo-100' }
];

const WhiteLabelPipeline: React.FC<WhiteLabelPipelineProps> = ({ searchTerm }) => {
  const [prospects, setProspects] = useState<WhiteLabelProspect[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProspects();
  }, []);

  const fetchProspects = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('white_label_prospects')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setProspects(data || []);
    } catch (error) {
      console.error('Error fetching prospects:', error);
      toast.error('Failed to load prospects');
    } finally {
      setLoading(false);
    }
  };

  const filteredProspects = prospects.filter(prospect =>
    prospect.organization_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    prospect.contact_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    prospect.contact_email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getProspectsByStage = (stageId: string) => {
    return filteredProspects.filter(prospect => prospect.pipeline_stage === stageId);
  };

  const handleStageChange = async (prospectId: string, newStage: string) => {
    try {
      const { error } = await supabase
        .from('white_label_prospects')
        .update({ pipeline_stage: newStage })
        .eq('id', prospectId);

      if (error) throw error;

      setProspects(prev => prev.map(prospect =>
        prospect.id === prospectId
          ? { ...prospect, pipeline_stage: newStage }
          : prospect
      ));

      toast.success('Prospect stage updated');
    } catch (error) {
      console.error('Error updating prospect stage:', error);
      toast.error('Failed to update prospect stage');
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'destructive';
      case 'high': return 'destructive';
      case 'medium': return 'default';
      case 'low': return 'secondary';
      default: return 'secondary';
    }
  };

  const formatCurrency = (amount?: number) => {
    return amount ? new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount) : 'TBD';
  };

  const ProspectCard = ({ prospect }: { prospect: WhiteLabelProspect }) => (
    <Card className="mb-3 cursor-pointer hover:shadow-md transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3 className="font-semibold text-sm">{prospect.organization_name}</h3>
            <p className="text-xs text-muted-foreground">{prospect.contact_name}</p>
          </div>
          <Badge variant={getPriorityColor(prospect.priority_level)} className="text-xs">
            {prospect.priority_level}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="pt-0 space-y-2">
        {/* Organization Type */}
        <div className="flex items-center gap-2 text-xs">
          <Building className="h-3 w-3 text-muted-foreground" />
          <span className="capitalize">{prospect.organization_type.replace('_', ' ')}</span>
        </div>

        {/* Event Volume */}
        {prospect.annual_event_volume && (
          <div className="flex items-center gap-2 text-xs">
            <Calendar className="h-3 w-3 text-muted-foreground" />
            <span>{prospect.annual_event_volume} events/year</span>
          </div>
        )}

        {/* Estimated Value */}
        <div className="flex items-center gap-2 text-xs">
          <DollarSign className="h-3 w-3 text-muted-foreground" />
          <span>{formatCurrency(prospect.estimated_value)}</span>
        </div>

        {/* Technology Readiness */}
        {prospect.technology_readiness && (
          <div className="space-y-1">
            <div className="flex justify-between text-xs">
              <span>Tech Readiness</span>
              <span>{prospect.technology_readiness}/5</span>
            </div>
            <Progress value={(prospect.technology_readiness / 5) * 100} className="h-1" />
          </div>
        )}

        {/* Contact Info */}
        <div className="space-y-1">
          {prospect.contact_email && (
            <div className="flex items-center gap-2 text-xs">
              <Mail className="h-3 w-3 text-muted-foreground" />
              <span className="truncate">{prospect.contact_email}</span>
            </div>
          )}
          {prospect.contact_phone && (
            <div className="flex items-center gap-2 text-xs">
              <Phone className="h-3 w-3 text-muted-foreground" />
              <span>{prospect.contact_phone}</span>
            </div>
          )}
        </div>

        {/* Geographic Markets */}
        {prospect.geographic_markets && prospect.geographic_markets.length > 0 && (
          <div className="flex items-center gap-2 text-xs">
            <MapPin className="h-3 w-3 text-muted-foreground" />
            <span className="truncate">{prospect.geographic_markets.join(', ')}</span>
          </div>
        )}

        {/* Next Action */}
        {prospect.next_action && (
          <div className="flex items-center gap-2 text-xs text-orange-600">
            <Clock className="h-3 w-3" />
            <span className="truncate">{prospect.next_action}</span>
            {prospect.next_action_date && (
              <span className="text-muted-foreground">
                ({new Date(prospect.next_action_date).toLocaleDateString()})
              </span>
            )}
          </div>
        )}

        {/* Tags */}
        {prospect.tags && prospect.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {prospect.tags.slice(0, 2).map((tag, index) => (
              <Badge key={index} variant="outline" className="text-xs">
                {tag}
              </Badge>
            ))}
            {prospect.tags.length > 2 && (
              <Badge variant="outline" className="text-xs">
                +{prospect.tags.length - 2}
              </Badge>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Pipeline Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
        {pipelineStages.map(stage => {
          const stageProspects = getProspectsByStage(stage.id);
          const totalValue = stageProspects.reduce((sum, p) => sum + (p.estimated_value || 0), 0);
          
          return (
            <Card key={stage.id} className={`${stage.color} border-0`}>
              <CardContent className="p-3 text-center">
                <div className="text-lg font-bold">{stageProspects.length}</div>
                <div className="text-xs text-muted-foreground truncate">{stage.name}</div>
                <div className="text-xs font-medium">{formatCurrency(totalValue)}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Pipeline Columns */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 min-h-[600px]">
        {pipelineStages.map(stage => {
          const stageProspects = getProspectsByStage(stage.id);
          
          return (
            <Card key={stage.id} className="flex flex-col">
              <CardHeader className={`${stage.color} rounded-t-lg`}>
                <CardTitle className="text-sm flex items-center justify-between">
                  <span>{stage.name}</span>
                  <Badge variant="secondary" className="text-xs">
                    {stageProspects.length}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="flex-1 p-2 space-y-2">
                {stageProspects.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground text-sm">
                    No prospects in this stage
                  </div>
                ) : (
                  stageProspects.map(prospect => (
                    <ProspectCard key={prospect.id} prospect={prospect} />
                  ))
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default WhiteLabelPipeline;